// SPDX-License-Identifier: GPL-3.0-or-later
// The sound effects. This file listens to the feedback bus and plays one synthesised patch for each
// event. It keeps the number of voices under a cap, it rate-limits the kinds that can storm, and it
// asks the engine to duck the music under a loud cue. It has no clock: it reads the audio time.
//
// THEME rule: audio is optional, and each meaningful audio cue needs a text cue. `SFX_CUES` says
// which kinds have sound and which text cue covers each meaningful one.

import type {
  FeedbackEvent,
  FeedbackKind,
  FeedbackListener,
} from "../../src/shared/feedback/index.ts";
import type { AudioEngine } from "./engine.ts";
import { type PatchInput, SFX_PATCHES } from "./sfx-bank.ts";
import { type Charger, createCharger } from "./sfx-charge.ts";
import { createVoice, makeNoiseBuffer, type Voice, type VoiceEnv } from "./sfx-voice.ts";

/**
 * Where the text cue of a sound is shown. The popup layer is gone: play is calm and no box shows
 * for a sound. A screen reader hears a "live-region" cue. A "callout" or a "hud" cue is on the page.
 */
export type TextCue = "callout" | "hud" | "live-region";

/** One row of the cue table. */
export interface SfxCue {
  /** False when the kind is silent on purpose. */
  readonly sound: boolean;
  /** True when the sound tells the player something that they must also be able to read. */
  readonly meaningful: boolean;
  /** The text cue that covers a meaningful sound. Null when the sound is not meaningful. */
  readonly text: TextCue | null;
  /** The least time between two sounds of this kind, in milliseconds. 0 means no limit. */
  readonly minGapMs: number;
  /** A loud cue dips the music. */
  readonly duck?: { readonly depth: number; readonly ms: number };
}

const feel = (minGapMs: number): SfxCue => ({
  sound: true,
  meaningful: false,
  text: null,
  minGapMs,
});
const moment = (text: TextCue, minGapMs = 0, duck?: SfxCue["duck"]): SfxCue => ({
  sound: true,
  meaningful: true,
  text,
  minGapMs,
  ...(duck ? { duck } : {}),
});

/** Which kinds have a sound, and which text cue covers each meaningful one. */
export const SFX_CUES: Readonly<Record<FeedbackKind, SfxCue>> = {
  bumper: feel(35),
  sling: feel(50),
  target: feel(40),
  drop_bank_complete: moment("live-region", 200),
  spinner: feel(55),
  ramp: feel(120),
  orbit: feel(120),
  lane: feel(60),
  kicker: feel(80),
  jackpot: moment("callout", 150, { depth: 0.5, ms: 900 }),
  combo: moment("callout", 100),
  skill_shot: moment("callout"),
  ball_lock: moment("callout"),
  multiball_start: moment("callout", 0, { depth: 0.6, ms: 1500 }),
  tilt_warning: moment("callout", 300),
  tilt: moment("callout", 0, { depth: 0.7, ms: 1200 }),
  extra_ball: moment("live-region"),
  stage_start: moment("callout"),
  stage_clear: moment("callout", 0, { depth: 0.5, ms: 900 }),
  draft_open: moment("hud"),
  perk_pick: moment("hud"),
  run_clear: moment("live-region", 0, { depth: 0.6, ms: 1500 }),
  hurry_up_start: moment("callout"),
  hurry_up_collect: moment("live-region", 150, { depth: 0.5, ms: 900 }),
  frenzy_start: moment("callout"),
  combo_chain: moment("hud", 100),
  timed_event_end: { sound: false, meaningful: false, text: null, minGapMs: 0 },
  extra_ball_lit: moment("callout"),
  level_best: moment("hud"),
  ball_start: moment("hud"),
  ball_save: moment("callout"),
  drain: moment("live-region", 0, { depth: 0.5, ms: 900 }),
  score: { sound: false, meaningful: false, text: null, minGapMs: 0 },
  mode_start: moment("callout"),
  mode_complete: moment("callout"),
  game_over: moment("callout", 0, { depth: 0.6, ms: 1800 }),
  high_score: moment("live-region"),
  featured_turn_start: moment("hud"),
  // The boss kinds. The boss sounds stay calm. A hit, a warning and a shield are soft.
  boss_start: moment("live-region", 0, { depth: 0.6, ms: 1500 }),
  boss_hit: feel(60),
  boss_phase: moment("live-region", 200),
  boss_telegraph: feel(200),
  boss_shield: feel(150),
  boss_defeat: moment("live-region", 0, { depth: 0.5, ms: 900 }),
  plunger_charge: feel(0),
  plunger_launch: feel(0),
  flipper_up: feel(45),
  flipper_down: feel(45),
};

/** The most voices that play at once. */
export const DEFAULT_MAX_VOICES = 16;
/** Strength that an event without a strength gets. */
export const DEFAULT_STRENGTH = 0.7;

/** What `createSfx` takes from the page. */
export interface SfxOptions {
  /** The most voices at once. Default 16. */
  readonly maxVoices?: number;
}

/** The sound effects. */
export interface Sfx {
  /** Stop listening and cut every voice. */
  dispose(): void;
  /** The number of voices that still sound. */
  activeVoices(): number;
  /** The audio time at which the last sounding voice ends. 0 when no voice sounds. */
  tailEnd(): number;
  /**
   * Cut every voice and the charge tone now. Use it for sounds that a suspend froze, so they do not
   * sound late.
   */
  cut(): void;
}

/** The part of the feedback bus that the sound effects use. */
export interface SfxSource {
  subscribe(listener: FeedbackListener): () => void;
}

function strengthOf(ev: FeedbackEvent): number {
  const s = ev.strength;
  if (s === undefined || !Number.isFinite(s)) return DEFAULT_STRENGTH;
  return Math.min(1, Math.max(0, s));
}

function patchInput(ev: FeedbackEvent, strength: number): PatchInput {
  const pitch = 0.92 + 0.16 * strength;
  return { strength, pitch, event: ev, f: (hz) => hz * pitch };
}

/** The level of a voice from the strength of the hit. */
export function voicePeak(strength: number): number {
  return 0.35 + 0.65 * strength;
}

/** The mutable state of one sound-effects instance. */
interface SfxState {
  readonly engine: AudioEngine;
  readonly maxVoices: number;
  readonly voices: Voice[];
  // Voices that a kill faded out. Each one is disposed when its fade has ended.
  readonly fading: Voice[];
  readonly lastAt: Map<FeedbackKind, number>;
  noise: AudioBuffer | null;
  charger: Charger | null;
  boundTo: BaseAudioContext | null;
}

function sfxBind(st: SfxState, ctx: BaseAudioContext, dest: AudioNode): VoiceEnv {
  if (st.boundTo !== ctx) {
    st.boundTo = ctx;
    st.noise = null;
    st.charger = createCharger(ctx, dest);
  }
  return {
    ctx,
    dest,
    noiseBuffer: () => {
      st.noise ??= makeNoiseBuffer(ctx);
      return st.noise;
    },
  };
}

// Dispose the voices of `list` whose end has passed, and remove them from the list.
function sfxDisposeEnded(list: Voice[], now: number): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const voice = list[i];
    if (voice && voice.end <= now) {
      voice.dispose();
      list.splice(i, 1);
    }
  }
}

function sfxPrune(st: SfxState, now: number): void {
  sfxDisposeEnded(st.voices, now);
  sfxDisposeEnded(st.fading, now);
}

/** The index of the quietest voice. */
function sfxQuietest(voices: readonly Voice[]): number {
  let quietest = 0;
  for (let i = 1; i < voices.length; i++) {
    if ((voices[i]?.peak ?? 0) < (voices[quietest]?.peak ?? 0)) quietest = i;
  }
  return quietest;
}

/** Make room for a voice of level `peak`. False means that the new voice is the quietest. */
function sfxAdmit(st: SfxState, peak: number, now: number): boolean {
  sfxPrune(st, now);
  if (st.voices.length < st.maxVoices) return true;
  const quietest = sfxQuietest(st.voices);
  const victim = st.voices[quietest];
  if (!victim || victim.peak > peak) return false;
  victim.kill(now);
  st.voices.splice(quietest, 1);
  st.fading.push(victim);
  return true;
}

function sfxTooSoon(st: SfxState, kind: FeedbackKind, cue: SfxCue, now: number): boolean {
  if (cue.minGapMs <= 0) return false;
  const last = st.lastAt.get(kind);
  if (last !== undefined && now - last < cue.minGapMs / 1000) return true;
  st.lastAt.set(kind, now);
  return false;
}

function sfxPlayPatch(
  st: SfxState,
  ev: FeedbackEvent,
  cue: SfxCue,
  env: VoiceEnv,
  now: number,
): void {
  const patch = SFX_PATCHES[ev.kind];
  if (!patch) return;
  const strength = strengthOf(ev);
  const peak = voicePeak(strength);
  if (!sfxAdmit(st, peak, now)) return;
  const voice = createVoice(env, now, peak);
  patch(voice, patchInput(ev, strength));
  st.voices.push(voice);
  if (cue.duck) st.engine.duck(cue.duck.depth, cue.duck.ms);
}

function sfxHandleCue(
  st: SfxState,
  ev: FeedbackEvent,
  cue: SfxCue,
  ctx: BaseAudioContext,
  dest: AudioNode,
): void {
  const env = sfxBind(st, ctx, dest);
  const now = ctx.currentTime;
  if (ev.kind === "plunger_charge") {
    st.charger?.update(strengthOf(ev), now);
    return;
  }
  if (ev.kind === "plunger_launch") st.charger?.release(now);
  if (sfxTooSoon(st, ev.kind, cue, now)) return;
  sfxPlayPatch(st, ev, cue, env, now);
}

/**
 * True for an event that makes no sound and no voice, even though its kind has a sound. A shield
 * that goes off is one. Its patch plays nothing, so a voice for it would only take a place in the
 * bank.
 */
function sfxSilent(ev: FeedbackEvent): boolean {
  return ev.kind === "boss_shield" && !ev.on;
}

function sfxHandle(st: SfxState, ev: FeedbackEvent): void {
  const ctx = st.engine.context();
  const dest = st.engine.sfxBus();
  if (!ctx || !dest) return;
  if (!st.engine.sfxEnabled || ctx.state !== "running") return;
  const cue = SFX_CUES[ev.kind];
  if (cue.sound && !sfxSilent(ev)) sfxHandleCue(st, ev, cue, ctx, dest);
}

// Cut every voice at once. No later prune runs, so each voice is disposed here. The charge tone is
// released too, so a hold that froze it does not let it sound after the pick.
function sfxCut(st: SfxState, now: number): void {
  st.charger?.release(now);
  for (const voice of st.voices) voice.kill(now);
  for (const voice of st.voices) voice.dispose();
  for (const voice of st.fading) voice.dispose();
  st.voices.length = 0;
  st.fading.length = 0;
}

// The latest end of the voices of `list`, or `end` when that is later.
function sfxLatestEnd(list: readonly Voice[], end: number): number {
  let latest = end;
  for (const voice of list) latest = Math.max(latest, voice.end);
  return latest;
}

function sfxDispose(st: SfxState, unsubscribe: () => void): void {
  unsubscribe();
  sfxCut(st, st.engine.context()?.currentTime ?? 0);
}

/** Listen to the feedback bus and play the sound effects. */
export function createSfx(engine: AudioEngine, source: SfxSource, options: SfxOptions = {}): Sfx {
  const st: SfxState = {
    engine,
    maxVoices: Math.max(1, options.maxVoices ?? DEFAULT_MAX_VOICES),
    voices: [],
    fading: [],
    lastAt: new Map(),
    noise: null,
    charger: null,
    boundTo: null,
  };
  const unsubscribe = source.subscribe((ev) => sfxHandle(st, ev));
  return {
    dispose: () => sfxDispose(st, unsubscribe),
    activeVoices() {
      sfxPrune(st, engine.context()?.currentTime ?? 0);
      return st.voices.length;
    },
    tailEnd() {
      sfxPrune(st, engine.context()?.currentTime ?? 0);
      return sfxLatestEnd(st.fading, sfxLatestEnd(st.voices, 0));
    },
    cut: () => sfxCut(st, engine.context()?.currentTime ?? 0),
  };
}
